import { useCallback, useMemo } from "react";
import { FullPageWithPaddingContainer } from "@shared/EDS/components/FullPageWithPaddingContainer";
import { Toolbar } from "@shared/EDS/components/Toolbar";
import { RetShift } from "@opstel/query/models/retShift";
import {
  DataTable,
  DataTableColumnDef,
  DataTableRow,
} from "@shared/EDS/components/DataTable/DataTable";
import { formatDuration } from "@shared/common/utils/luxonHelpers";
import { DateTime } from "luxon";
import { RetShiftActions } from "./RetShiftActions";
import { TractieSearchInput } from "@tractie/components";
import { useFilterState } from "@shared/common/hooks/useFilterState";
import { DateRadioPanel } from "@shared/common/components/DateRadioPanel";

interface RetShiftAssignmentsProps {
  readonly data: RetShift[];
}

const columns: DataTableColumnDef<RetShift>[] = [
  {
    header: "",
    accessorKey: "id",
    enableSorting: false,
    cell({ row }) {
      return (
        <div style={{ width: "32px" }}>
          {row.getCanExpand() && (
            <tractie-button
              data-testid={`ret-shift-expand-button-${row.original.id}`}
              level="tertiary"
              icon-only
              aria-label={`open-task-by-id-${row.original.id}`}
            >
              <tractie-icon name={row.getIsExpanded() ? "chevron-up" : "chevron-down"} />
            </tractie-button>
          )}
        </div>
      );
    },
    maxSize: 24,
  },
  {
    id: "serviceNumber",
    header: "Dienstnummer",
    accessorKey: "serviceNumber",
    minSize: 180,
    maxSize: 180,
  },
  {
    header: "Begintijd",
    accessorKey: "start",
    cell({ row }) {
      return `${row.original.start.toFormat("ccc HH:mm")}`;
    },
    minSize: 110,
    maxSize: 110,
    filterFn: (row, _columnId, filterValue?: DateTime) => {
      if (!filterValue) return true;
      return row.original.start.hasSame(filterValue, "day");
    },
    sortFn: (rowA, rowB) => {
      return rowA.original.start.toSeconds() - rowB.original.start.toSeconds();
    },
  },
  {
    header: "Eindtijd",
    accessorKey: "end",
    cell({ row }) {
      return `${row.original.end.toFormat("ccc HH:mm")}`;
    },
    minSize: 110,
    maxSize: 110,
    sortFn: (rowA, rowB) => {
      return rowA.original.end.toSeconds() - rowB.original.end.toSeconds();
    },
  },
  {
    header: "Duur",
    accessorKey: "duration",
    cell: ({ row }) => {
      const duration = row.original.end.diff(row.original.start);
      const formattedDuration = formatDuration(duration, "%h%M%S");
      return formattedDuration;
    },
    sortFn: (rowA, rowB) => {
      const durationA = rowA.original.end.diff(rowA.original.start);
      const durationB = rowB.original.end.diff(rowB.original.start);
      return durationA.as("seconds") - durationB.as("seconds");
    },
    minSize: 130,
    maxSize: 130,
  },
  {
    header: "Aantal klussen",
    accessorKey: "actions",
    cell({ row }) {
      return `${row.original.actions.length}`;
    },
    enableSorting: true,
    filterFn: (row, _columnId, filterValue: string) => {
      if (filterValue === "") return true;
      return row.original.actions.some(
        (action) =>
          action.trainNumber.includes(filterValue) ||
          action.trainNumberDeparture.includes(filterValue),
      );
    },
    minSize: 800,
  },
];

function filterZeroDurationActions(retShifts: RetShift[]): RetShift[] {
  return retShifts.map((retShift) => {
    return {
      ...retShift,
      actions: retShift.actions.filter((i) => i.start.diff(i.end).as("seconds") < 0),
    };
  });
}

export function RetShiftAssignments({ data: unfilteredData }: RetShiftAssignmentsProps) {
  const [currentDateFilter, setDateFilter] = useFilterState<DateTime | undefined>({
    id: "start",
    value: undefined,
  });
  const [searchInput, setSearchInput] = useFilterState({ id: "actions", value: "" });

  const data = useMemo(() => filterZeroDurationActions(unfilteredData), [unfilteredData]);

  const getRetShiftsActions = useCallback(
    (row: DataTableRow<RetShift>) => (
      <>
        <td></td>
        <td className="pt-m pb-l" colSpan={columns.length - 1}>
          <RetShiftActions
            actions={row.original.actions}
            trainNumberSearchString={searchInput.value}
          />
        </td>
      </>
    ),
    [searchInput],
  );

  return (
    <FullPageWithPaddingContainer>
      <div className="flex items-center gap-xs">
        <h3 className="m-0 font-medium">Werklijnen</h3>
        {data.length > 0 && (
          <tractie-tag color="blue" appearance="subtle" label={data.length.toString()} size="m" />
        )}
      </div>
      <Toolbar>
        <div className="flex w-full items-center justify-between">
          <DateRadioPanel dateTimes={data.map((d) => d.start)} onChange={setDateFilter} />
          <tractie-search-input
            className="min-w-70"
            placeholder="Zoeken op treinnummer"
            modelValue={searchInput.value}
            onmodel-value-changed={(e) =>
              setSearchInput((e.target as TractieSearchInput).value ?? "")
            }
          />
        </div>
      </Toolbar>
      <DataTable
        data-testid="ret-shift-assignments-table"
        columns={columns}
        data={data}
        defaultSort={[{ id: "serviceNumber", desc: false }]}
        enableExpanding={true}
        getRowCanExpand={(row) => row.original.actions.length > 0}
        getSubTable={getRetShiftsActions}
        filters={[searchInput, currentDateFilter]}
        options={{
          emptyMessage: "Geen werklijnen gevonden voor deze zoekopdracht",
        }}
      />
    </FullPageWithPaddingContainer>
  );
}
